import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {buildAccountMismatchIdentities} from '@perfectpost/perfect-post-common';
import {CustomContentProps, useSnackbar} from 'notistack';
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {SnackbarContent as CustomSnackbarContent} from './SnackbarContent';
import {AccountMismatchProfiles} from '../extension/AccountMismatchProfiles';
import {OpenExtensionPopupButton} from '../extension/OpenExtensionPopupButton';

interface LinkedinSessionUnavailableProps extends CustomContentProps {
  csrfTokenExists: boolean;
  extensionVersion: string;
  isAuthenticated: boolean;
  perfectPostEmail?: string;
  linkedInVanity?: string;
  accountMismatch: boolean;
  missingAccountAssociation: boolean;
  accountStatus: 'ok' | 'mismatch' | 'missing_association' | 'no_session';
  accountMismatchDetails?: {
    sessionVanity?: string;
    sessionEntityUrn?: string;
    currentVanity?: string;
    currentEntityUrn?: string;
  };
}

export const LinkedinSessionUnavailable = React.forwardRef<HTMLDivElement, LinkedinSessionUnavailableProps>(
  (props, ref) => {
    const {id} = props;
    const debugInfo = props;
    const {closeSnackbar} = useSnackbar();
    const {t} = useTranslation();
    // Le profil enregistré est identifié par son `entityUrn`, pas par son `vanityName` : c'est
    // l'`entityUrn` qui a servi à décider le refus, et lui seul qui distingue les deux profils.
    const mismatchIdentities = useMemo(
      () =>
        buildAccountMismatchIdentities({
          registeredEntityUrn: debugInfo.accountMismatchDetails?.sessionEntityUrn,
          registeredVanityName: debugInfo.accountMismatchDetails?.sessionVanity ?? debugInfo.linkedInVanity,
          currentEntityUrn: debugInfo.accountMismatchDetails?.currentEntityUrn,
          currentVanityName: debugInfo.accountMismatchDetails?.currentVanity,
        }),
      [debugInfo.accountMismatchDetails, debugInfo.linkedInVanity],
    );
    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);
    const noSession = !debugInfo.csrfTokenExists && !debugInfo.accountMismatch;
    // Session présente mais aucun profil associé : ce n'est pas un compte « différent ».
    const missingAssociationOnly = !noSession && !debugInfo.accountMismatch && debugInfo.missingAccountAssociation;

    return (
      <CustomSnackbarContent
        ref={ref}
        color="error"
        icon={<WarningIcon />}
        title={
          noSession
            ? t('notification.debug.no_linkedin_session')
            : missingAssociationOnly
              ? t('notification.linkedinmissingassociation.title')
              : t('notification.linkedinmismatch.title')
        }
        content={
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
            <Typography variant="body2">
              {noSession
                ? t('notification.debug.no_linkedin_session_explanation')
                : missingAssociationOnly
                  ? t('notification.linkedinmissingassociation.content')
                  : t('notification.linkedinmismatch.content')}
            </Typography>

            {/* Session LinkedIn absente : on rassure sur le rôle de l'extension + comment reprendre */}
            {noSession && (
              <>
                <Box sx={{p: 2, bgcolor: 'action.hover', borderRadius: 1}}>
                  <Typography variant="body2">{t('notification.debug.extension_scope')}</Typography>
                </Box>
                <Typography variant="body2">{t('notification.debug.no_linkedin_session_howto')}</Typography>
                <OpenExtensionPopupButton variant="outlined" size="small" sx={{mt: 1}} />
              </>
            )}

            {debugInfo.accountMismatch && (
              <AccountMismatchProfiles
                identities={mismatchIdentities}
                registeredLabel={t('notification.debug.extension_linkedin')}
                currentLabel={t('notification.debug.current_browser_account')}
              />
            )}

            {debugInfo.missingAccountAssociation && (
              <Box sx={{p: 2, bgcolor: 'alert.warningBg', color: 'warning.main', borderRadius: 1, mb: 2}}>
                <Typography variant="body2" fontWeight="bold">
                  {t('notification.debug.missing_association')}
                </Typography>
              </Box>
            )}

            <Button
              component="a"
              variant="contained"
              size="small"
              color="primary"
              href="https://www.linkedin.com/"
              target="_blank"
              sx={{mt: 2}}
            >
              {noSession || missingAssociationOnly
                ? t('notification.linkedinmismatch.connect_account_btn')
                : t('notification.linkedinmismatch.change_account_btn')}
            </Button>
          </Box>
        }
        onClose={handleDismiss}
        bgWhite
        titleColor="error"
        dividerColor={false}
      />
    );
  },
);
